import { produce } from 'immer'
import { useState } from 'react'

import type {
  ControllerConfigurationModel,
  ControllerConfigurationModelUpdate,
  ControllerConfigurationSourceText,
  ControllerConfigurationSourceUpdate,
} from '@/features/controller/configuration/models/controllerConfiguration'
import {
  readControllerConfigurationModelFromSource,
  writeControllerConfigurationModelToSource,
} from '@/features/controller/configuration/models/controllerConfiguration'

type useMultiModalConfiguration = {
  initialSource: ControllerConfigurationSourceUpdate
}

interface UseMultiModalConfiguration {
  applyConfigurationModelUpdate: (update: ControllerConfigurationModelUpdate) => void
  /**
   * Returns the model read from the given source. A caller that needs to decide something about the
   * configuration it has just applied must use this rather than the hook's `multiModalConfiguration`,
   * which still holds the previous render's value until React re-renders.
   */
  applyConfigurationSourceUpdate: (update: ControllerConfigurationSourceUpdate) => ControllerConfigurationModel
  multiModalConfiguration: MultiModalConfiguration
}

interface MultiModalConfiguration {
  source: ControllerConfigurationSourceText
  model: ControllerConfigurationModel
}

export function useMultiModalConfiguration({ initialSource }: useMultiModalConfiguration): UseMultiModalConfiguration {
  const [multiModalConfiguration, setMultiModalConfiguration] = useState<MultiModalConfiguration>({
    source: initialSource,
    model: readControllerConfigurationModelFromSource(initialSource),
  })

  function applyConfigurationSourceUpdate(update: ControllerConfigurationSourceText) {
    const model = readControllerConfigurationModelFromSource(update)
    setMultiModalConfiguration({ source: update, model })
    return model
  }

  function applyConfigurationModelUpdate({
    updateAppConfig,
    updateRaspiPropertiesConfig,
  }: ControllerConfigurationModelUpdate) {
    setMultiModalConfiguration(({ source: prevSource, model: prevModel }) => {
      const updatedModel = produce(prevModel, (draft) => {
        if (updateAppConfig) {
          updateAppConfig(draft.appConfig!)
        }
        if (updateRaspiPropertiesConfig) {
          updateRaspiPropertiesConfig(draft.raspiPropertiesConfig!)
        }
      })
      return {
        model: updatedModel,
        source: writeControllerConfigurationModelToSource(updatedModel, prevSource.raspiPropertiesText),
      }
    })
  }

  return {
    applyConfigurationModelUpdate,
    applyConfigurationSourceUpdate,
    multiModalConfiguration,
  }
}
